import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

interface CreateTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (
    examFamilyId: string,
    displayName: string,
    modality: string,
    bodyRegion: string,
    contrastPhase: string
  ) => Promise<boolean>;
  creating: boolean;
}

const MODALITIES = ["CT", "MR", "XR", "US", "NM", "FL", "MG", "PET"];

const BODY_REGIONS = [
  "HEAD",
  "NECK",
  "CHEST",
  "ABDOMEN",
  "PELVIS",
  "SPINE",
  "UPPER_EXTREMITY",
  "LOWER_EXTREMITY",
  "WHOLE_BODY",
];

const CONTRAST_PHASES = [
  "WO_CONTRAST",
  "W_CONTRAST",
  "W_WO_CONTRAST",
  "ARTERIAL",
  "VENOUS",
  "DELAYED",
];

export function CreateTemplateModal({
  open,
  onOpenChange,
  onCreate,
  creating,
}: CreateTemplateModalProps) {
  const [examFamilyId, setExamFamilyId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [modality, setModality] = useState("");
  const [bodyRegion, setBodyRegion] = useState("");
  const [contrastPhase, setContrastPhase] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!examFamilyId.trim()) {
      toast.error("Exam Family ID is required");
      return;
    }
    if (!displayName.trim()) {
      toast.error("Display Name is required");
      return;
    }
    if (!modality) {
      toast.error("Modality is required");
      return;
    }
    if (!bodyRegion) {
      toast.error("Body Region is required");
      return;
    }

    // Format exam_family_id (uppercase, underscores)
    const formattedId = examFamilyId
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "_")
      .replace(/[^A-Z0-9_]/g, "");

    const success = await onCreate(
      formattedId,
      displayName.trim(),
      modality,
      bodyRegion,
      contrastPhase
    );

    if (success) {
      // Reset form
      setExamFamilyId("");
      setDisplayName("");
      setModality("");
      setBodyRegion("");
      setContrastPhase("");
      onOpenChange(false);
    }
  };

  const handleClose = () => {
    if (!creating) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create New Template</DialogTitle>
            <DialogDescription>
              Create a new exam family template. The template ID should be unique
              and follow the naming convention (e.g., CT_CHEST_WO).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="examFamilyId">Exam Family ID</Label>
              <Input
                id="examFamilyId"
                value={examFamilyId}
                onChange={(e) => setExamFamilyId(e.target.value)}
                placeholder="e.g., CT_CHEST_WO"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Unique identifier. Will be converted to uppercase with underscores.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name</Label>
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g., CT Chest without Contrast"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Modality</Label>
                <Select value={modality} onValueChange={setModality}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select modality" />
                  </SelectTrigger>
                  <SelectContent>
                    {MODALITIES.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Body Region</Label>
                <Select value={bodyRegion} onValueChange={setBodyRegion}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {BODY_REGIONS.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Contrast Phase (Optional)</Label>
              <Select value={contrastPhase} onValueChange={setContrastPhase}>
                <SelectTrigger>
                  <SelectValue placeholder="Select contrast phase" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {CONTRAST_PHASES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c.replace(/_/g, " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={creating}>
              {creating ? "Creating..." : "Create Template"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
